import React, { useEffect, useState, createElement, Component } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { BookingPage } from './pages/BookingPage';
import { ThankYouPage } from './pages/ThankYouPage';
import { LandingPageIN } from './pages/in/LandingPageIN';
import { BookingPageIN } from './pages/in/BookingPageIN';
import { ThankYouPageIN } from './pages/in/ThankYouPageIN';
import { SEOHead } from './components/SEOHead';
// SEO config per route
const SEO_CONFIG: Record<string, {
  title: string;
  description: string;
}> = {
  '/': {
    title: 'Faceless YouTube System — Build a Profitable YouTube Channel Without Showing Your Face',
    description: 'Get a done-for-you faceless YouTube channel designed to grow, monetize, and earn. Data-backed niche strategy, content production, and fast monetization. No face, no filming, no guesswork.'
  },
  '/booking': {
    title: 'Book Your Free Strategy Call — Faceless YouTube System',
    description: 'Schedule a free strategy call to map out your faceless YouTube channel. Get a personalized niche analysis, monetization roadmap, and channel setup blueprint.'
  },
  '/thank-you': {
    title: 'Booking Confirmed — Faceless YouTube System',
    description: 'Your strategy call has been booked successfully. Check your email for confirmation and join our WhatsApp group.'
  },
  '/in': {
    title: 'Faceless YouTube System India — Build a Profitable YouTube Channel | ₹500 Strategy Call',
    description: 'Exclusive for Indian creators. Get a done-for-you faceless YouTube channel with personalized niche analysis, monetization roadmap, and expert guidance. Book your ₹500 strategy call today.'
  },
  '/in/booking': {
    title: 'Book Your ₹500 Strategy Call — Faceless YouTube System India',
    description: 'Schedule your ₹500 strategy call for a personalized niche analysis, custom monetization roadmap, channel setup blueprint, and 30-min 1-on-1 with an expert.'
  },
  '/in/thank-you': {
    title: 'Booking Confirmed — Faceless YouTube System India',
    description: 'Your ₹500 strategy call has been booked successfully. Check your email for confirmation and message us on WhatsApp.'
  }
};
// Map global routes to their Indian equivalents
const GLOBAL_TO_IN_ROUTES: Record<string, string> = {
  '/': '/in',
  '/booking': '/in/booking',
  '/thank-you': '/in/thank-you'
};
async function detectCountry(): Promise<string> {
  const cached = sessionStorage.getItem('user_country');
  if (cached) return cached;
  const apis = [{
    url: 'https://ipapi.co/json/',
    extract: (d: any) => d?.country_code
  }, {
    url: 'https://ipinfo.io/json?token=',
    extract: (d: any) => d?.country
  }, {
    url: 'https://freeipapi.com/api/json',
    extract: (d: any) => d?.countryCode
  }];
  for (const api of apis) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const res = await fetch(api.url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!res.ok) continue;
      const data = await res.json();
      const country = api.extract(data) || '';
      if (country) {
        sessionStorage.setItem('user_country', country);
        return country;
      }
    } catch {
      continue;
    }
  }
  sessionStorage.setItem('user_country', 'UNKNOWN');
  return 'UNKNOWN';
}
// Inject favicon, viewport, charset, and structured data once
function injectGlobalHead() {
  // Charset
  if (!document.querySelector('meta[charset]')) {
    const charset = document.createElement('meta');
    charset.setAttribute('charset', 'UTF-8');
    document.head.prepend(charset);
  }
  // Viewport
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0';
    document.head.appendChild(viewport);
  }
  // Theme color
  if (!document.querySelector('meta[name="theme-color"]')) {
    const theme = document.createElement('meta');
    theme.name = 'theme-color';
    theme.content = '#0a0a0f';
    document.head.appendChild(theme);
  }
  // Language
  document.documentElement.lang = 'en';
  // Favicon — SVG play button icon (YouTube-themed)
  if (!document.querySelector('link[rel="icon"]')) {
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/svg+xml';
    favicon.href = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <rect width="100" height="100" rx="20" fill="#0a0a0f"/>
        <polygon points="38,25 38,75 78,50" fill="url(%23grad)"/>
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:%233b82f6"/>
            <stop offset="100%" style="stop-color:%23a855f7"/>
          </linearGradient>
        </defs>
      </svg>`)}`;
    document.head.appendChild(favicon);
  }
  // Apple touch icon (same SVG as fallback)
  if (!document.querySelector('link[rel="apple-touch-icon"]')) {
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <rect width="100" height="100" rx="20" fill="#0a0a0f"/>
        <polygon points="38,25 38,75 78,50" fill="%233b82f6"/>
      </svg>`)}`;
    document.head.appendChild(apple);
  }
  // Structured Data — Organization + WebSite
  if (!document.querySelector('script[type="application/ld+json"]')) {
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'Organization',
        name: 'Faceless YouTube System',
        description: 'Build a profitable faceless YouTube channel with done-for-you content production and data-backed strategy.',
        url: window.location.origin
      }, {
        '@type': 'WebSite',
        name: 'Faceless YouTube System',
        url: window.location.origin,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${window.location.origin}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      }]
    });
    document.head.appendChild(jsonLd);
  }
}
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isIndian, setIsIndian] = useState<boolean | null>(null);
  // Get SEO config for current route
  const seo = SEO_CONFIG[location.pathname] || SEO_CONFIG['/'];
  const canonical = `${window.location.origin}${location.pathname}`;
  // Inject global head tags once
  useEffect(() => {
    injectGlobalHead();
  }, []);
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Detect country once on mount
  useEffect(() => {
    detectCountry().then(country => {
      setIsIndian(country === 'IN');
    });
  }, []);
  // Redirect Indian users on every route change
  useEffect(() => {
    if (isIndian === true) {
      const inRoute = GLOBAL_TO_IN_ROUTES[location.pathname];
      if (inRoute) {
        navigate(inRoute, {
          replace: true
        });
      }
    }
  }, [isIndian, location.pathname, navigate]);
  // Initialize Meta Pixel
  useEffect(() => {
    const f = window as any;
    if (f.fbq) return;
    const n: any = f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
    f.fbq('init', '3786982544942566');
    f.fbq('track', 'PageView');
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=3786982544942566&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, []);
  // Preload the booking script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.yourmarketingai.com/js/form_embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);
  }, []);
  // External tracking script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.yourmarketingai.com/js/external-tracking.js';
    script.setAttribute('data-tracking-id', 'tk_b13770ceb66d4f2e92cf63e0247127ed');
    document.body.appendChild(script);
  }, []);
  return <>
      <SEOHead title={seo.title} description={seo.description} canonical={canonical} data-id="element-0" />
      <Routes data-id="element-1">
        <Route path="/" element={<LandingPage onNavigateToBooking={() => navigate('/booking')} data-id="element-3" />} data-id="element-2" />
        <Route path="/booking" element={<BookingPage onBack={() => navigate('/')} data-id="element-5" />} data-id="element-4" />
        <Route path="/thank-you" element={<ThankYouPage onBack={() => navigate('/')} data-id="element-7" />} data-id="element-6" />

        {/* Indian Audience Funnel */}
        <Route path="/in" element={<LandingPageIN data-id="element-9" />} data-id="element-8" />
        <Route path="/in/booking" element={<BookingPageIN data-id="element-11" />} data-id="element-10" />
        <Route path="/in/thank-you" element={<ThankYouPageIN data-id="element-13" />} data-id="element-12" />
      </Routes>
    </>;
}
export function App() {
  return <AppContent data-id="element-14" />;
}